import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  clearRedeemPoint,
  requestGetPointBalance,
  requestGetRewardsProduct,
  requestRedeemPoint,
  useAuthState,
  usePointDispatch,
  usePointState,
} from "../../Context";
import FormLabel from "../../Components/FormLabel";
import FormPhoneInput from "../../Components/v2/Form/FormPhoneInput";
import { classNames, getErrorMessages } from "../../Utils";
import { Button } from "../../Components/Button";
import InputSkeleton from "../../Components/InputSkeleton";
import { InformationCircleIcon } from "@heroicons/react/outline";
import { Alert } from "../../Components/Alert";
import { ModalConfirm } from "../../Components/ModalConfirm";

const RewardRedemptionForm = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pointDispatch = usePointDispatch();

  const [name, setname] = useState("");
  const [email, setemail] = useState("");
  const [phone, setphone] = useState("");
  const [address, setaddress] = useState("");

  const [nameError, setnameError] = useState(null);
  const [emailError, setemailError] = useState(null);
  const [phoneError, setphoneError] = useState(null);
  const [addressError, setaddressError] = useState(null);
  const [submitted, setsubmitted] = useState(false);
  const [showSuccessModal, setshowSuccessModal] = useState(false);

  const {
    balance,
    loading,
    error,
    product,
    productDetailLoading,
    productDetailError,
    redemption,
    redemptionLoading,
    redemptionError,
  } = usePointState();
  const { user } = useAuthState();

  const cleanedPhone = phone?.replace(/\s/g, "");
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const fetchProductDetails = useCallback(() => {
    if (id) {
      requestGetPointBalance(pointDispatch);
      requestGetRewardsProduct(pointDispatch, { id });
    }
  }, [id, pointDispatch]);

  useEffect(() => {
    fetchProductDetails();
  }, [fetchProductDetails]);

  useEffect(() => {
    return () => {
      clearRedeemPoint(pointDispatch);
    };
  }, [pointDispatch]);

  useEffect(() => {
    if (user) {
      setname(user.name || "");
      setemail(user.email || "");
      setphone(user.phone || "");
      setaddress(user.address || "");
    }
  }, [user]);

  useEffect(() => {
    if (submitted) {
      const isNameValid = Boolean(name);
      const isPhoneValid = cleanedPhone?.length >= 10;
      const isEmailValid = emailRegex.test(email);

      if (isNameValid) {
        setErrorMessage("name", "");
      } else {
        setErrorMessage("name", "nameRequired");
      }

      if (email) {
        if (isEmailValid) {
          setErrorMessage("email", "");
        } else {
          setErrorMessage("email", "Please enter a valid email address.");
        }
      } else {
        setErrorMessage("email", "Email is required.");
      }

      if (phone) {
        if (isPhoneValid) {
          setErrorMessage("phone", "");
        } else {
          setErrorMessage("phone", "Please enter a valid phone number.");
        }
      } else {
        setErrorMessage("phone", "phoneNumberRequired");
      }

      if (product?.type === "PHYSICAL") {
        if (!address || address.trim() === "") {
          setErrorMessage("address", t("addressRequired"));
        } else {
          setErrorMessage("address", "");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, phone, email, address, submitted, product?.type]);

  useEffect(() => {
    if (redemption?.id) {
      setshowSuccessModal(true);
    }
  }, [redemption?.id]);

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "name":
        setnameError(message);
        break;
      case "phone":
        setphoneError(message);
        break;
      case "email":
        setemailError(message);
        break;
      case "address":
        setaddressError(message);
        break;
      default:
        break;
    }
  };

  const validateInput = () => {
    const isCustomerValid = Boolean(name);
    const isPhoneValid = phone && cleanedPhone?.length >= 10;
    const isEmailValid = email && emailRegex.test(email);
    const isAddressValid =
      product?.type === "PHYSICAL" ? Boolean(address?.trim()) : true;

    return isCustomerValid && isPhoneValid && isEmailValid && isAddressValid;
  };

  const handleSubmit = () => {
    setsubmitted(true);

    if (!validateInput()) {
      return;
    }

    const input = {
      rewardProductID: product?.id,
      quantity: 1,
      detail: {
        name,
        phone,
        email,
        address: {
          street: address,
        },
      },
    };

    requestRedeemPoint(pointDispatch, { input });
  };

  const getButtonTitle = () => {
    if (product.availableStock === 0) return "outOfStock";
    if (balance?.balance < product?.point && product?.availableStock > 0)
      return "insufficientPoints";
    return "redeemPoints";
  };

  const ProductCardSkeleton = () => {
    return (
      <div className="animate-pulse p-4 flex flex-col bg-white rounded-lg text-sm divide-y">
        <div>
          <div className="h-48 bg-gray-300 rounded-md mb-4" />
          <div className="h-4 w-20 bg-gray-200 rounded-md" />
          <div className="h-4 w-28 bg-gray-200 rounded-md mt-1 mb-2" />
        </div>
        <div className="flex flex-col gap-1">
          <div className="h-4 w-20 bg-gray-200 rounded-md mt-2" />
          <div className="h-4 w-20 bg-gray-200 rounded-md" />
        </div>
      </div>
    );
  };

  if (error || productDetailError) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={error?.message || productDetailError?.message}
          errors={error?.errors || productDetailError?.errors}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
      {redemption?.id && (
        <ModalConfirm
          open={showSuccessModal}
          setOpen={(open) => {
            setshowSuccessModal(open);
          }}
          title="Success"
          description="redemptionSuccess"
          type="SUCCESS"
          onConfirm={() => {
            if (redemption?.id) {
              navigate(`/rewards-redemption/${redemption.id}`);
              clearRedeemPoint(pointDispatch);
            }
          }}
        />
      )}
      <div className="lg:col-start-3 lg:col-span-1">
        {productDetailLoading ? (
          <ProductCardSkeleton />
        ) : (
          <a
            href={`/rewards-product/${product.id}`}
            className="p-4 flex flex-col bg-white rounded-lg text-sm divide-y"
          >
            <div className="mb-2">
              {product.imageUrl ? (
                <div className="relative rounded-md w-full h-auto aspect-video overflow-hidden mb-2">
                  <img
                    className="absolute object-contain w-full h-full z-10"
                    src={product.imageUrl}
                    alt="Product"
                    loading="lazy"
                  />
                  <div
                    className="absolute w-full h-full bg-fill blur-[50px] brightness-90 mask-gradient"
                    style={{
                      backgroundImage: `url('${product.imageUrl}')`,
                    }}
                  />
                </div>
              ) : (
                <div className="rounded-md w-full h-auto aspect-video text-center overflow-hidden bg-gray-200 text-sm text-gray-500 flex flex-col justify-center mb-2">
                  {t("No Image")}
                </div>
              )}
              <span className="text-gray-900 font-medium">{product?.name}</span>
              {product?.availableStock ? (
                product.availableStock < 10 ? (
                  <div className="flex space-x-1 text-amber-500 font-medium items-center">
                    <InformationCircleIcon className="h-4 w-4" />
                    <span>{t("remaining")}</span>
                    <span>{product.availableStock}</span>
                  </div>
                ) : (
                  <div className="flex space-x-1 text-green-500 font-medium items-center">
                    <span>{t("available")}</span>
                  </div>
                )
              ) : null}
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col-reverse mt-2">
                <span className="font-medium text-blue-500">
                  {product.point?.toLocaleString("id-ID")} {t("point")}
                </span>
                {product.basePoint && product.basePoint > product.point ? (
                  <span className="text-xs line-through text-gray-400">
                    {product.basePoint?.toLocaleString("id-ID")} {t("point")}
                  </span>
                ) : null}
              </div>
            </div>
          </a>
        )}
      </div>
      <div className="flex flex-col space-y-6 lg:col-start-1 lg:col-span-2 rounded-lg bg-white">
        <div className="space-y-4 px-4 py-6">
          {redemptionError && (
            <Alert
              title="Error"
              type="error"
              message={
                t(getErrorMessages(redemptionError)) ||
                "Error checkout product."
              }
              errors={redemptionError?.errors}
            />
          )}
          {productDetailLoading ? (
            <InputSkeleton rows={4} />
          ) : (
            <>
              <div>
                <FormLabel htmlFor="name" label={t("Name")} required />
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={name}
                  onChange={(e) => setname(e.target.value)}
                  placeholder={t("Enter your name")}
                  className={classNames(
                    nameError
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                    "mt-1 block w-full text-sm rounded-md shadow-sm"
                  )}
                  required
                />
                {nameError && (
                  <div className="mt-1 text-red-600 text-sm">
                    {t(nameError)}
                  </div>
                )}
              </div>
              <div>
                <FormLabel htmlFor="email" label={t("Email")} required />
                <input
                  type="email"
                  name="email"
                  data-testid="email"
                  value={email}
                  placeholder={t("Enter email")}
                  onChange={(e) => setemail(e.target.value)}
                  className={classNames(
                    emailError
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                    "mt-1 block w-full text-sm rounded-md shadow-sm"
                  )}
                />
                {emailError && (
                  <div className="mt-1 text-red-600 text-sm">
                    {t(emailError)}
                  </div>
                )}
              </div>
              <div>
                <FormLabel htmlFor="phone" label={t("Phone Number")} required />
                <FormPhoneInput
                  id="phone"
                  name="phone"
                  data-testid="phone"
                  value={phone}
                  onChange={setphone}
                  placeholder={t("Enter Phone Number")}
                  className={classNames(
                    phoneError
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                    "mt-1 block w-full text-sm rounded-md shadow-sm"
                  )}
                />
                {phoneError && (
                  <div className="mt-1 text-red-600 text-sm">
                    {t(phoneError)}
                  </div>
                )}
              </div>
              <div>
                <FormLabel
                  htmlFor="address"
                  label={t("Address")}
                  required={Boolean(product?.type === "PHYSICAL")}
                />
                <textarea
                  name="address"
                  id="address"
                  value={address}
                  onChange={(e) => setaddress(e.target.value)}
                  placeholder={t("Enter Address")}
                  className={classNames(
                    addressError
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                    "mt-1 block w-full text-sm rounded-md shadow-sm py-2 px-3"
                  )}
                />
                {addressError && (
                  <div className="mt-1 text-red-600 text-sm">
                    {t(addressError)}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="flex bg-gray-50 rounded-b-md sm:rounded-b-lg p-4">
          <Button
            className="flex-1"
            title={getButtonTitle()}
            loading={productDetailLoading || loading || redemptionLoading}
            disabled={
              productDetailLoading ||
              loading ||
              (balance?.balance < product?.point &&
                product?.availableStock > 0) ||
              product.availableStock === 0 ||
              redemptionLoading
            }
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default RewardRedemptionForm;
